import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { saveLastRead } from "utils/lastRead";

const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8000"
    : process.env.REACT_APP_API_URL;

function VersesDisplay(props) {
  // accept either props or route params
  const params = useParams();
  const propBookId = props.bookId;
  const propChapter = props.chapter;
  const persistLastRead = props.persistLastRead === true;

  // Effective identifiers: prefer props (when embedded), otherwise route params.
  const effectiveBookId = propBookId != null ? propBookId : params.bookId;
  const effectiveChapter =
    propChapter != null ? propChapter : params.chapterNumber;

  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVerse, setSelectedVerse] = useState(null);

  useEffect(() => {
    // If required identifiers are missing, show a friendly error
    if (!effectiveBookId || !effectiveChapter) {
      setLoading(false);
      setError("Libro o capítulo no especificado.");
      setVerses([]);
      return;
    }

    setLoading(true);
    setSelectedVerse(null);

    axios
      .get(
        `${API_URL}/api/books/${effectiveBookId}/chapters/${effectiveChapter}/`
      )
      .then((res) => {
        setVerses(res.data.verses);
        setError(null);

        // Only persist lastRead if instructed to (BookChapters context)
        if (persistLastRead) {
          try {
            const bookName =
              res.data.book_name || res.data.bookName || props.bookName || null;
            saveLastRead({
              bookId: Number(effectiveBookId),
              bookName: bookName,
              chapter: Number(effectiveChapter),
            });
          } catch (e) {
            // swallow errors
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching verses:", err);
        setError("No se pudieron cargar los versículos.");
        setVerses([]);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBookId, effectiveChapter]);

  if (loading) {
    return (
      <p className="text-center font-bembo text-lg text-neutral-600">
        Cargando versículos...
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-center font-bembo text-lg text-red-600">{error}</p>
    );
  }

  if (!verses.length) {
    return (
      <p className="text-center font-bembo text-lg text-neutral-600">
        Sin versículos para mostrar
      </p>
    );
  }

  // Split verses roughly in half
  const half = Math.ceil(verses.length / 2);
  const leftColumn = verses.slice(0, half);
  const rightColumn = verses.slice(half);

  const handleClick = (verse) => {
    setSelectedVerse(verse.verse_number);
    // Copy verse text to clipboard
    const textToCopy = `${verse.verse_number} ${verse.text}`;
    navigator.clipboard.writeText(textToCopy).catch((error) => {
      console.error("Error copying text:", error);
    });

    // only persist verse-level lastRead when in BookChapters (persistLastRead)
    if (persistLastRead) {
      try {
        saveLastRead({
          bookId: Number(effectiveBookId),
          chapter: Number(effectiveChapter),
          verse: verse.verse_number,
        });
      } catch (e) {}
    }
  };

  const renderVerses = (versesList) =>
    versesList.map((verse) => (
      <p
        key={verse.verse_number}
        onClick={() => handleClick(verse)}
        className={`cursor-pointer rounded p-1 text-xl font-bembo text-neutral-700 leading-relaxed text-center max-w-[90%] 
            ${selectedVerse === verse.verse_number ? "bg-blue-100" : ""} 
            hover:bg-slate-100`}
        style={{ userSelect: "text" }}
        title="Haz clic para copiar este versículo"
      >
        <span className="font-bold font-bembo text-lg text-neutral-800">
          {verse.verse_number}
        </span>{" "}
        {verse.text}
      </p>
    ));

  return (
    <div className="max-w-3xl mx-auto mt-6 relative">
      {/* Faint dividing line only on md and up */}
      <div className="hidden md:block absolute left-1/2 top-0 h-full w-[1px] bg-slate-300 opacity-70"></div>

      <div className="flex flex-col md:flex-row justify-center">
        <div className="w-full md:w-1/2 p-4 flex flex-col items-center space-y-3">
          {renderVerses(leftColumn)}
        </div>
        {rightColumn.length > 0 && (
          <div className="w-full md:w-1/2 p-4 flex flex-col items-center space-y-3">
            {renderVerses(rightColumn)}
          </div>
        )}
      </div>
    </div>
  );
}

export default VersesDisplay;
